/**
 * useFormFactor — detect whether the app is running in kiosk or companion mode.
 *
 * Detection priority:
 *   1. Explicit `?mode=kiosk` query param (the kiosk launcher app
 *      passes this when opening the patient experience).
 *   2. Persisted preference in sessionStorage (so a kiosk that loses
 *      the query param mid-session doesn't flip).
 *   3. Heuristic — touchscreen + width >= 1280 px → kiosk.
 *
 * Per the plan §2: a single responsive build serves both form factors.
 * The kiosk variant bumps tap targets, hides browser chrome, and
 * enables the idle-logout watchdog.
 */

import { useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'cardioq-patient-form-factor';

/**
 * Three form factors:
 *   · kiosk    — 43" touchscreen at point of care (full-screen, big taps)
 *   · desktop  — production-grade web app on a normal display (default)
 *   · mobile   — narrow viewport (<768px) — bottom-nav responsive variant
 */
export type FormFactor = 'kiosk' | 'desktop' | 'mobile';

export interface FormFactorInfo {
  isKiosk: boolean;
  isDesktop: boolean;
  isMobile: boolean;
  /** Default tap-target px — 60 on kiosk, 44 elsewhere. */
  tapTarget: 60 | 44;
  /** Default idle timeout — kiosk only. */
  idleTimeoutMs: number;
}

function detectInitial(): FormFactor {
  if (typeof window === 'undefined') return 'desktop';
  const params = new URLSearchParams(window.location.search);
  const mode = params.get('mode');
  if (mode === 'kiosk' || mode === 'desktop' || mode === 'mobile') {
    try {
      sessionStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
    return mode;
  }
  try {
    const persisted = sessionStorage.getItem(STORAGE_KEY) as FormFactor | null;
    if (persisted === 'kiosk' || persisted === 'desktop' || persisted === 'mobile') {
      return persisted;
    }
  } catch {
    /* ignore */
  }
  // Heuristic stack:
  //   1. Large touchscreen → kiosk (43" at point of care)
  //   2. Narrow viewport → mobile (phones)
  //   3. Everything else → desktop (production default)
  if (typeof window.matchMedia === 'function') {
    if (window.matchMedia('(min-width: 1280px) and (pointer: coarse)').matches) {
      return 'kiosk';
    }
    if (window.matchMedia('(max-width: 767px)').matches) {
      return 'mobile';
    }
  }
  return 'desktop';
}

export function useFormFactor(): FormFactorInfo {
  const [formFactor, setFormFactor] = useState<FormFactor>(() => detectInitial());

  // Re-evaluate on resize / orientation change. Important for tablets
  // that could plausibly be either.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setFormFactor(detectInitial());
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
    };
  }, []);

  return useMemo<FormFactorInfo>(
    () => ({
      isKiosk: formFactor === 'kiosk',
      isDesktop: formFactor === 'desktop',
      isMobile: formFactor === 'mobile',
      tapTarget: formFactor === 'kiosk' ? 60 : 44,
      idleTimeoutMs: formFactor === 'kiosk' ? 90_000 : 0,
    }),
    [formFactor],
  );
}
